<template>
  <entity-list-table
    class="staff-list"
    :title="title"
    :data-service="dataService"
    :headers="headers"
    :create-route-name="createRouteName"
    :edit-route-name="editRouteName"
    :allow-delete="allowDelete"
    :show-status-column="showStatusColumn"
    :edit-permission="editPermission"
    :allow-toggle-status="allowToggleStatus"
    :items-filter-fn="itemsFilterFn"
  >
    <template v-slot:top-filter>
      <staff-top-filter @applyFilter="setFilter" />
    </template>
    <template v-slot:item.roles="{ item }">
      <span v-if="item.roles">
        <v-chip
          v-for="role in item.roles"
          :key="role.id"
          class="staff-list_role-item"
          small
          color="success"
        >
          {{ role.title }}
        </v-chip>
      </span>
    </template>

    <template v-slot:item.filials="{ item }">
      <div v-if="item.filials" class="staff-list_filial-list">
        <v-chip
          v-for="filial in item.filials"
          :key="filial.id"
          class="staff-list_filial-item"
          small
        >
          {{ filial.name }}
        </v-chip>
      </div>
    </template>

    <!-- Кнопка повторяет вид «Изменить»: залитый оранжевый кружок 28px с белой иконкой
         на узких экранах, текстовая кнопка на xl. fab, а не icon: у icon фон прозрачный -->
    <template v-if="canLoginAs" v-slot:extra-actions="{ item }">
      <v-tooltip top>
        <template v-slot:activator="{ on, attrs }">
          <v-btn
            fab
            color="primary"
            elevation="1"
            width="28"
            height="28"
            class="d-xl-none mr-2"
            :disabled="item.id === currentUserId || !item.status"
            :loading="loginAsId === item.id"
            v-bind="attrs"
            v-on="on"
            @click="loginAs(item)"
          >
            <!-- eslint-disable-next-line -->
            <v-icon small>mdi-login-variant</v-icon>
          </v-btn>
          <v-btn
            depressed
            small
            color="primary"
            class="d-none d-xl-inline-flex mr-2"
            :disabled="item.id === currentUserId || !item.status"
            :loading="loginAsId === item.id"
            v-bind="attrs"
            v-on="on"
            @click="loginAs(item)"
          >
            Войти
          </v-btn>
        </template>
        <span>Войти под сотрудником</span>
      </v-tooltip>
    </template>
  </entity-list-table>
</template>

<script>
  import UsersDataService from '@/services/api/UsersDataService'
  import EntityListTable from '@/components/ui/crud/EntityListTable.vue'
  import StaffTopFilter from '@/components/staff/StaffTopFilter.vue'
  import { mapGetters } from 'vuex'
  import CRMPermission from '@/models/enums/permissions/CRMPermission'

  export default {
    components: { EntityListTable, StaffTopFilter },
    data () {
      return {
        title: 'Сотрудники',
        dataService: UsersDataService,
        createRouteName: 'user-create',
        editRouteName: 'user-edit',

        headers: [
          { text: 'ID', value: 'id', align: 'start' },
          { text: 'Имя', value: 'name', align: 'start' },
          { text: 'Телефон', value: 'phone_formatted', align: 'start' },
          { text: 'Должность', value: 'roles', align: 'start' },
          { text: 'Филиал', value: 'filials', align: 'start' },
        ],

        editPermission: CRMPermission.EditUsers,
        allowDelete: false,
        showStatusColumn: true,
        allowToggleStatus: true,
        loginAsId: null,

        filter: {
          filialId: null,
          filialPreset: null,
          roleIds: [],
        },
      }
    },
    computed: {
      ...mapGetters({
        getTimezoneTitle: 'app/getTimezoneTitle',
        getBranches: 'branches/getBranches',
        currentUser: 'auth/getUser',
      }),
      currentUserId () {
        return this.currentUser?.id ?? null
      },
      canLoginAs () {
        return this.userCan(CRMPermission.LoginAsUser)
      },
      // id филиалов выбранного пресета; null - пресет не задан или «все»
      presetFilialIds () {
        if (!this.filter.filialPreset || this.filter.filialPreset === 'all') {
          return null
        }

        const franchise = this.filter.filialPreset === 'franchise'

        return this.getBranches
          .filter((filial) => Boolean(filial.franchise) === franchise)
          .map((filial) => filial.id)
      },
      itemsFilterFn () {
        const { filialId, roleIds } = this.filter
        const presetIds = this.presetFilialIds

        if (!filialId && !presetIds && roleIds.length === 0) {
          return null
        }

        return (user) =>
          this.matchesRoles(user, roleIds) &&
          this.matchesFilials(user, filialId, presetIds)
      },
    },
    methods: {
      setFilter (filter) {
        this.filter = filter
      },
      async loginAs (item) {
        this.loginAsId = item.id

        try {
          await this.$store.dispatch('auth/loginAs', item.id)
          // Полная перезагрузка, как после обычного логина: стор и сокеты поднимаются под новым пользователем
          window.location.href = '/'
        } catch (err) {
          this.showErrorFromResponse(err)
          this.loginAsId = null
        }
      },
      matchesRoles (user, roleIds) {
        if (roleIds.length === 0) {
          return true
        }

        return (user.roles || []).some((role) => roleIds.includes(role.id))
      },
      matchesFilials (user, filialId, presetIds) {
        if (filialId) {
          return (user.filials || []).some((filial) => filial.id === filialId)
        }

        if (presetIds) {
          return (user.filials || []).some((filial) => presetIds.includes(filial.id))
        }

        return true
      },
    },
  }
</script>
<style lang="less" scoped>
.staff-list {
  &_role-item {
    margin: 5px 0 0 0;
  }

  &_filial-list {
    padding: 5px;
  }

  &_filial-item {
    margin-left: 5px;
    margin-top: 5px;
    font-size: 11px;
  }

  .staff-list_role-item {
    margin-right: 5px;
  }
}
</style>
